import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  PUBLIC_SITE_CONTENT_COLLECTION,
  LOGIN_PAGE_DOC_ID,
  DEFAULT_LOGIN_FOOTER_INNER_HTML,
  DEFAULT_LOGIN_NOTICE_HTML,
  DEFAULT_APP_LAYOUT_FOOTER_HTML,
  resolveLoginFooterHtml,
  resolveLoginNoticeHtml,
  resolveAppLayoutFooterHtml,
} from '../constants/loginPageContent';
import { Globe, Save, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Admin/superuser: edit HTML for the public login/register page (footer + optional notice).
 */
export default function LoginPageContentEditor() {
  const [footerHtml, setFooterHtml] = useState(DEFAULT_LOGIN_FOOTER_INNER_HTML);
  const [noticeHtml, setNoticeHtml] = useState(DEFAULT_LOGIN_NOTICE_HTML);
  const [appLayoutFooterHtml, setAppLayoutFooterHtml] = useState(DEFAULT_APP_LAYOUT_FOOTER_HTML);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const ref = doc(db, PUBLIC_SITE_CONTENT_COLLECTION, LOGIN_PAGE_DOC_ID);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : null;
        if (cancelled) return;
        setFooterHtml(resolveLoginFooterHtml(data));
        setNoticeHtml(resolveLoginNoticeHtml(data));
        setAppLayoutFooterHtml(resolveAppLayoutFooterHtml(data));
      } catch (e) {
        console.error('LoginPageContentEditor load error:', e);
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await setDoc(
        ref,
        {
          footerHtml,
          noticeHtml,
          appLayoutFooterHtml,
          updatedAt: serverTimestamp(),
          updatedByEmail: auth.currentUser?.email || null,
        },
        { merge: true }
      );
      setMessage('Saved. Changes appear on the login page and in the app footer within a few seconds.');
    } catch (e) {
      console.error('LoginPageContentEditor save error:', e);
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setFooterHtml(DEFAULT_LOGIN_FOOTER_INNER_HTML);
    setNoticeHtml(DEFAULT_LOGIN_NOTICE_HTML);
    setAppLayoutFooterHtml(DEFAULT_APP_LAYOUT_FOOTER_HTML);
  };

  if (loading) {
    return (
      <div className="text-sm text-slate-500 py-8">Loading site content…</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <Globe className="text-slate-600 shrink-0 mt-0.5" size={22} />
          <div>
            <h2 className="text-lg font-bold text-slate-800">Public site &amp; app footer</h2>
            <p className="text-sm text-slate-500 mt-1">
              HTML for the public sign-in page and the compact footer bar on signed-in pages (dashboard, analytics, map). You can include links (e.g. to policy pages on the main site) and basic tags such as{' '}
              <code className="text-xs bg-slate-100 px-1 rounded">&lt;p&gt;</code>,{' '}
              <code className="text-xs bg-slate-100 px-1 rounded">&lt;a&gt;</code>,{' '}
              <code className="text-xs bg-slate-100 px-1 rounded">&lt;ul&gt;</code>.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {message && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <CheckCircle size={16} className="shrink-0 mt-0.5" /> {message}
          </div>
        )}

        <div className="space-y-2 mb-6">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Message above footer (optional)
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Shown below the “By signing in…” line. Leave the textarea empty and save to hide this block.
          </p>
          <textarea
            value={noticeHtml}
            onChange={(e) => setNoticeHtml(e.target.value)}
            rows={5}
            className="w-full font-mono text-xs border border-slate-200 rounded-lg p-3 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            spellCheck="false"
          />
        </div>

        <div className="space-y-2 mb-6">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Login page · full footer HTML
          </label>
          <textarea
            value={footerHtml}
            onChange={(e) => setFooterHtml(e.target.value)}
            rows={16}
            className="w-full font-mono text-xs border border-slate-200 rounded-lg p-3 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            spellCheck="false"
          />
        </div>

        <div className="space-y-2 mb-6">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Signed-in app · compact footer (one line)
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Shown at the bottom of the main layout on the dashboard, analytics, and map. Keep it short; it can wrap on small screens.
          </p>
          <textarea
            value={appLayoutFooterHtml}
            onChange={(e) => setAppLayoutFooterHtml(e.target.value)}
            rows={6}
            className="w-full font-mono text-xs border border-slate-200 rounded-lg p-3 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            spellCheck="false"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-[#043132] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#065354] disabled:opacity-60"
          >
            <Save size={16} /> {saving ? 'Saving…' : 'Save to Firestore'}
          </button>
          <button
            type="button"
            onClick={handleResetDefaults}
            className="text-sm text-slate-600 hover:text-slate-900 underline"
          >
            Reset text areas to built-in defaults
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Preview (message)</p>
        <div
          className="login-notice-preview rounded bg-white p-3 border border-slate-100 text-sm"
          dangerouslySetInnerHTML={{ __html: noticeHtml || '<p class="text-slate-400 italic">(empty, nothing will show)</p>' }}
        />
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-4 mb-2">Preview (footer)</p>
        <div
          className="login-app-footer rounded overflow-hidden"
          dangerouslySetInnerHTML={{ __html: footerHtml }}
        />
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-4 mb-2">Preview (signed-in app footer)</p>
        <div className="rounded border border-slate-200 overflow-hidden">
          <div
            className="app-layout-footer"
            dangerouslySetInnerHTML={{ __html: appLayoutFooterHtml }}
          />
        </div>
      </div>
    </div>
  );
}
